import React, { useEffect } from 'react'
import { toCommas } from '../../utils/utils'
import styles from './Dashboard.module.css'
import { useHistory } from 'react-router-dom'
import { useSelector, useDispatch } from 'react-redux'
import { getWhitesByUser } from '../../actions/whiteActions'
// Revenue analytics imports
import FiltersBar from "../analytics/FiltersBar";
import RevenueTrendChart from "../analytics/RevenueTrendChart";
import LeaderboardList from "../analytics/LeaderboardList";
import PaymentList from "../analytics/PaymentList";
import CustomerInvoicesCard from "../analytics/CustomerInvoicesCard";
import FlaggedInvoicesCard from "../analytics/FlaggedInvoicesCard";
import OverdueInvoicesCard from "../analytics/OverdueInvoicesCard";
import OutstandingInvoicesCard from "../analytics/OutstandingInvoicesCard";
import RecentPaymentsCard from "../analytics/RecentPaymentsCard";
import useRevenueAnalytics from "../../hooks/useRevenueAnalytics";
import { fetchPcModelInventory, fetchProductModelOptions, fetchProductSearchAnalytics } from "../../api";


// MUI v4 helpers (adjust if you already import these elsewhere)
import { Box, Grid, ButtonGroup, Button, TextField } from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";

// import Donut from './Donut'
import moment from 'moment'
import { Bag } from './Icons'
import useAuthUser from '../../hooks/useAuthUser'


const Dashboard = () => {

    const history = useHistory()
    const dispatch = useDispatch()
    const authData = useAuthUser()
    const { whites = [] } = useSelector((state) => state?.whites || { whites: [] })
    const currYear = new Date().getFullYear();
    // ----- Revenue widget controls -----
    const [mode, setMode] = React.useState("accrual");                     // 'cash' | 'accrual'
    const [accrualField, setAccrualField] = React.useState("receivedByDate");
    const [rangePreset, setRangePreset] = React.useState("30d");         // '30d' | '90d' | 'ytd'
    const [grouping, setGrouping] = React.useState("day");               // 'day' | 'week' | 'month'
    const [useCustom, setUseCustom] = React.useState(false);
    const [fromCustom, setFromCustom] = React.useState("");
    const [toCustom, setToCustom] = React.useState("");
    const [statusIn, setStatusIn] = React.useState([]);
    const [topProductMetric, setTopProductMetric] = React.useState("revenue");
    const [topCustomerMetric, setTopCustomerMetric] = React.useState("revenue");
    const [productSearchModel, setProductSearchModel] = React.useState("");
    const [productSearchFrom, setProductSearchFrom] = React.useState("");
    const [productSearchTo, setProductSearchTo] = React.useState("");
    const [productSearchResult, setProductSearchResult] = React.useState(null);
    const [productSearchLoading, setProductSearchLoading] = React.useState(false);
    const [productSearchError, setProductSearchError] = React.useState("");
    const [pcModelSearchModel, setPcModelSearchModel] = React.useState("");
    const [pcModelSearchResult, setPcModelSearchResult] = React.useState(null);
    const [pcModelSearchLoading, setPcModelSearchLoading] = React.useState(false);
    const [pcModelSearchError, setPcModelSearchError] = React.useState("");
    const [productModelOptions, setProductModelOptions] = React.useState([]);

    const currencyFmt = (v, code = "USD") =>
    new Intl.NumberFormat(undefined, { style: "currency", currency: code, maximumFractionDigits: 0 }).format(v || 0);
    const currencyPreciseFmt = (v, code = "USD") =>
    new Intl.NumberFormat(undefined, { style: "currency", currency: code, minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v || 0);
    const shortDate = (value) => (value ? moment(value).format("MM/DD/YYYY") : "—");
    const normalizePcModelCandidate = React.useCallback((candidate) => {
        if (!candidate) return null;
        if (typeof candidate === "string") {
            const modelNumber = candidate.trim().toUpperCase();
            return modelNumber ? { modelNumber, description: "" } : null;
        }

        const modelNumber = String(
            candidate.modelNumber ||
            candidate.model ||
            candidate.modelNo ||
            candidate.prd ||
            candidate.product ||
            candidate.sku ||
            ""
        ).trim().toUpperCase();

        if (!modelNumber) return null;

        return {
            modelNumber,
            description: String(candidate.description || candidate.modelDescription || candidate.productDescription || candidate.desc || "").trim(),
            foq: String(candidate.foq || candidate.FOQ || candidate.raw?.foq || candidate.raw?.FOQ || "").trim().toUpperCase(),
        };
    }, []);

    const getPcModelCandidates = React.useCallback((result) => {
        const candidates = Array.isArray(result?.modelCandidates)
            ? result.modelCandidates
            : Array.isArray(result?.data?.partialModelList)
                ? result.data.partialModelList
                : [];
        const seen = new Set();
        return candidates
            .map(normalizePcModelCandidate)
            .filter((candidate) => {
                if (!candidate?.modelNumber || seen.has(candidate.modelNumber)) return false;
                if (candidate.foq === "Q" || candidate.foq === "*") return false;
                if (Number(candidate.farmingdaleInv) < 10) return false;
                seen.add(candidate.modelNumber);
                return true;
            });
    }, [normalizePcModelCandidate]);

    const runProductSearch = React.useCallback(async () => {
        const modelNumber = String(productSearchModel || "").trim();
        if (!modelNumber) {
            setProductSearchError("Enter a model number.");
            return;
        }

        setProductSearchLoading(true);
        setProductSearchError("");
        try {
            const fromISO = productSearchFrom ? new Date(`${productSearchFrom}T00:00:00`).toISOString() : undefined;
            const toISO = productSearchTo ? new Date(`${productSearchTo}T23:59:59.999`).toISOString() : undefined;
            const { data } = await fetchProductSearchAnalytics({
                modelNumber,
                from: fromISO,
                to: toISO,
                limit: 25,
            });
            setProductSearchResult(data || null);
        } catch (err) {
            setProductSearchError(err?.response?.data?.message || err?.message || "Failed to search product analytics");
            setProductSearchResult(null);
        } finally {
            setProductSearchLoading(false);
        }
    }, [productSearchModel, productSearchFrom, productSearchTo]);

    const runPcModelSearch = React.useCallback(async (modelOverride) => {
        const modelNumber = String(modelOverride || pcModelSearchModel || "").trim().toUpperCase();
        if (!modelNumber) {
            setPcModelSearchError("Enter a model number.");
            return;
        }

        setPcModelSearchLoading(true);
        setPcModelSearchError("");
        try {
            const { data } = await fetchPcModelInventory(modelNumber);
            setPcModelSearchResult(data || null);
        } catch (err) {
            setPcModelSearchError(err?.response?.data?.message || err?.message || "Failed to search PC inventory");
            setPcModelSearchResult(null);
        } finally {
            setPcModelSearchLoading(false);
        }
    }, [pcModelSearchModel]);

    const selectPcModelCandidate = React.useCallback((modelNumber) => {
        const nextModel = String(modelNumber || "").trim().toUpperCase();
        if (!nextModel) return;
        setPcModelSearchModel(nextModel);
        runPcModelSearch(nextModel);
    }, [runPcModelSearch]);

    React.useEffect(() => {
        const query = String(pcModelSearchModel || "").trim();
        if (query.length < 2) {
            setProductModelOptions([]);
            return undefined;
        }

        let active = true;
        const timer = window.setTimeout(async () => {
            try {
                const { data } = await fetchProductModelOptions({ q: query, limit: 20 });
                if (active) setProductModelOptions(data?.data || []);
            } catch (error) {
                if (active) setProductModelOptions([]);
            }
        }, 250);

        return () => {
            active = false;
            window.clearTimeout(timer);
        };
    }, [pcModelSearchModel]);

    const now = new Date();
    const { fromISO, toISO } = React.useMemo(() => {
    if (useCustom && fromCustom && toCustom) {
        const start = new Date(fromCustom + "T00:00:00");
        const end = new Date(new Date(toCustom).getTime() + 24*60*60*1000 - 1);
        return { fromISO: start.toISOString(), toISO: end.toISOString() };
    }
    let start;
    if (rangePreset === "30d") start = new Date(now.getTime() - 29*864e5);
    else if (rangePreset === "90d") start = new Date(now.getTime() - 89*864e5);
    else start = new Date(now.getFullYear(), 0, 1);
    return { fromISO: start.toISOString(), toISO: new Date(now.getTime()+1).toISOString() };
    }, [useCustom, fromCustom, toCustom, rangePreset]);

    // Fetch analytics
    const { data, loading, error } = useRevenueAnalytics({
    endpoint: "/api/analytics/revenue",
    mode,
    accrualField,
    fromISO,
    toISO,
    grp: grouping,
    statusIn
    });

    const profitSeries = React.useMemo(
      () => (data?.profitSeries ?? []).map(d => ({ ...d, period: new Date(d.period) })),
      [data?.profitSeries]
    );
    const totalProfit = React.useMemo(
      () => profitSeries.reduce((sum, p) => sum + (Number(p.revenue) || 0), 0),
      [profitSeries]
    );

    // Derive view models
    const series = React.useMemo(
      () => (data?.series ?? []).map(d => ({ ...d, period: new Date(d.period) })),
      [data?.series]
    );

    const revenueEntries = React.useMemo(() => {
      const entries = [];
      [...(series || [])]
        .sort((a, b) => new Date(a.period || 0) - new Date(b.period || 0))
        .forEach((bucket) => {
          const period = bucket.period;
          (bucket.invoiceDetails || []).forEach((inv) => {
            entries.push({
              invoiceId: inv.invoiceId,
              invoiceNumber: inv.invoiceNumber,
              clientStore: inv.clientStore || inv.clientName,
              amount: inv.amount,
              accrualDate: period,
            });
          });
        });
      return entries;
    }, [series]);

    const profitEntries = React.useMemo(() => {
      const entries = [];
      [...(profitSeries || [])]
        .sort((a, b) => new Date(a.period || 0) - new Date(b.period || 0))
        .forEach((bucket) => {
          const period = bucket.period;
          (bucket.invoiceDetails || []).forEach((inv) => {
            entries.push({
              invoiceId: inv.invoiceId,
              invoiceNumber: inv.invoiceNumber,
              clientStore: inv.clientStore,
              amount: inv.amount,
              accrualDate: period,
            });
          });
        });
      return entries;
    }, [profitSeries]);

    const topProductsRows = React.useMemo(() => {
    const list = (data?.topProducts ?? []).map(p => ({
        name: p.name || p._id,
        id: p._id,
        revenue: Number(p.revenue) || 0,
        qty: Number(p.qty) || 0,
        profit: typeof p.margin === "number" ? p.margin : 0,
        margin: p.margin,
    }));
    const metricKey = topProductMetric === "quantity" ? "qty" : topProductMetric === "profit" ? "profit" : "revenue";
    return list
        .sort((a, b) => (b[metricKey] || 0) - (a[metricKey] || 0))
        .map(item => ({
            left: item.name,
            right: metricKey === "qty" ? Math.round(item.qty) : item[metricKey],
            sub: `Qty ${Math.round(item.qty)}${typeof item.margin === "number" ? ` • Gross Margin ${currencyFmt(item.margin, "USD")}` : ""}`,
            meta: { modelNumber: item.id }
        }));
    }, [data?.topProducts, topProductMetric]);
    const topCustomersRows = React.useMemo(() => {
      const metricKey = topCustomerMetric === "quantity" ? "qty" : topCustomerMetric === "profit" ? "profit" : "revenue";
      return [...(data?.topCustomers || [])]
        .sort((a, b) => (b[metricKey] || 0) - (a[metricKey] || 0))
        .map((customer) => {
          const locDetails = (customer.locations || [])
            .sort((a, b) => (b[metricKey] || 0) - (a[metricKey] || 0))
            .slice(0, 3)
            .map((location) => `${location.location || location.name || "Location"}: ${metricKey === "qty" ? Math.round(location.qty || 0) : currencyFmt(location[metricKey] || 0, "USD")}`)
            .join(" • ")
          return {
            left: customer._id || customer.name || "Unknown customer",
            right: metricKey === "qty" ? Math.round(customer.qty || 0) : customer[metricKey],
            sub: locDetails || `Qty ${Math.round(customer.qty || 0)} • Profit ${currencyFmt(customer.profit || 0, "USD")}`
          }
        })
    }, [data?.topCustomers, topCustomerMetric]);



    const handleProductRowClick = React.useCallback(
        (row) => {
            const model = row?.meta?.modelNumber || row?.left;
            if (!model) return;
            history.push(`/products?focus=${encodeURIComponent(model)}`);
        },
        [history]
    );

    const renderPcModelSearchWidget = () => {
        const summary = pcModelSearchResult?.inventorySummary;
        const totals = summary?.totals || {};
        const onOrderRows = pcModelSearchResult?.scheduledOnOrderRows || pcModelSearchResult?.onOrderRows || [];
        const modelCandidates = getPcModelCandidates(pcModelSearchResult);
        const selectedProductModelOption = productModelOptions.find(
            (product) => String(product?.modelNumber || "").trim().toUpperCase() === pcModelSearchModel
        ) || null;
        const showCandidateSelection = modelCandidates.length > 0 && (
            !summary ||
            pcModelSearchResult?.onOrderLookupStatus === "skipped-select-model" ||
            Array.isArray(pcModelSearchResult?.data?.partialModelList)
        );

        return (
            <div className={styles.pcModelWidget}>
                <div className={styles.pcModelHeader}>
                    <div>
                        <h3>PC Model Search</h3>
                        <p>Search live PC inventory by model number.</p>
                    </div>
                    {pcModelSearchResult?.modelNumber ? (
                        <span>{pcModelSearchResult.modelNumber}</span>
                    ) : null}
                </div>
                <div className={styles.pcModelControls}>
                    <Autocomplete
                        freeSolo
                        options={productModelOptions}
                        value={selectedProductModelOption}
                        inputValue={pcModelSearchModel}
                        getOptionLabel={(option) => typeof option === "string" ? option : option?.modelNumber || ""}
                        onInputChange={(event, value) => {
                            setPcModelSearchModel(String(value || "").trim().toUpperCase());
                        }}
                        onChange={(event, value) => {
                            const modelNumber = typeof value === "string" ? value : value?.modelNumber;
                            setPcModelSearchModel(String(modelNumber || "").trim().toUpperCase());
                        }}
                        renderOption={(option) => (
                            <div className={styles.pcModelOption}>
                                <strong>{option.modelNumber}</strong>
                                {option.description || option.brand ? (
                                    <span>{[option.brand, option.description].filter(Boolean).join(" - ")}</span>
                                ) : null}
                            </div>
                        )}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Model Number"
                                variant="outlined"
                                size="small"
                                onKeyDown={(e) => {
                                    if (e.key === "Enter") {
                                        e.preventDefault();
                                        runPcModelSearch();
                                    }
                                }}
                            />
                        )}
                        noOptionsText={pcModelSearchModel ? "No saved product matches. Search typed model." : "Type a model number"}
                    />
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={() => runPcModelSearch()}
                        disabled={pcModelSearchLoading}
                    >
                        {pcModelSearchLoading ? "Searching..." : "Search"}
                    </Button>
                </div>
                {pcModelSearchError ? (
                    <div className={styles.pcModelError}>{pcModelSearchError}</div>
                ) : null}
                {showCandidateSelection ? (
                    <div className={styles.pcModelCandidates}>
                        <h4>Select Matching Model</h4>
                        {modelCandidates.map((candidate) => (
                            <button
                                key={candidate.modelNumber}
                                type="button"
                                onClick={() => selectPcModelCandidate(candidate.modelNumber)}
                            >
                                <strong>{candidate.modelNumber}</strong>
                                {candidate.description ? <span>{candidate.description}</span> : null}
                            </button>
                        ))}
                    </div>
                ) : null}
                {summary && !showCandidateSelection ? (
                    <div className={styles.pcModelResults}>
                        <div className={styles.pcModelGrid}>
                            <div><strong>Available</strong><span>{totals.balance ?? 0}</span></div>
                            <div><strong>Fdale qty</strong><span>{totals.farmingdaleQuantity ?? totals.quantity ?? 0}</span></div>
                            <div><strong>NJ qty</strong><span>{totals.njQuantity ?? summary?.stores?.cartaret?.quantity ?? 0}</span></div>
                            <div><strong>Scheduled qty</strong><span>{totals.scheduled ?? 0}</span></div>
                            <div><strong>ASAP qty</strong><span>{totals.asap ?? 0}</span></div>
                            <div><strong>On order</strong><span>{totals.onOrder ?? 0}</span></div>
                            <div><strong>On-order lookup</strong><span>{pcModelSearchResult?.onOrderLookupStatus || "not checked"}</span></div>
                        </div>
                        <div className={styles.pcModelSchedule}>
                            <h4>Scheduled On Order</h4>
                            {onOrderRows.length ? (
                                <table>
                                    <thead>
                                        <tr>
                                            <th>Date</th>
                                            <th>Model</th>
                                            <th>Location</th>
                                            <th>Qty</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {onOrderRows.map((row, index) => (
                                            <tr key={`${row.modelNumber}-${row.arrival}-${row.locationNum}-${index}`}>
                                                <td>{row.arrival}</td>
                                                <td>{row.modelNumber}</td>
                                                <td>{row.locationNum}</td>
                                                <td>{row.planQty}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            ) : pcModelSearchResult?.onOrderLookupStatus === "checked" ? (
                                <div className={styles.pcModelEmpty}>No scheduled on-order rows found.</div>
                            ) : (
                                <div className={styles.pcModelEmpty}>No on-order lookup was needed.</div>
                            )}
                        </div>
                    </div>
                ) : null}
            </div>
        );
    };

    const ytdWhiteRevenue = React.useMemo(() => {
        return (whites || []).reduce((sum, white) => {
            if (moment(white?.receivedByDate).year() === currYear) {
                return sum + (Number(white?.total) || 0)
            }
            return sum
        }, 0)
    }, [whites, currYear])

    useEffect(() => {
        if (!authData) {
            history.push('/login')
            return
        }
        const searchId = authData?.result?._id || authData?.result?.googleId
        if (searchId) {
            dispatch(getWhitesByUser({search: searchId}));
        }
    }, [dispatch, authData, history]);

    const dashboardSummary = data?.dashboard || {};

    return (
        <div className={styles.pageContainer}>
           
    
            <section className={styles.stat}>
                <h1 style={{textAlign: 'center'}}> Dashboard </h1>
                <Box style={{ display: "grid", gridGap: 16, marginTop: 16, width: "90%", margin: "10px auto" }}>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                            <OverdueInvoicesCard currency="USD" autoLoad={false} invoices={dashboardSummary.overdueInvoices || []} loading={loading} />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <OutstandingInvoicesCard currency="USD" autoLoad={false} groups={dashboardSummary.outstandingByCustomer || []} loading={loading} />
                        </Grid>
                    </Grid>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                            <FlaggedInvoicesCard currency="USD" autoLoad={false} invoices={dashboardSummary.flaggedInvoices || []} loading={loading} />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <CustomerInvoicesCard currency="USD" autoLoad={false} invoices={dashboardSummary.customerInvoices || []} loading={loading} />
                        </Grid>
                    </Grid>
                <FiltersBar
                    mode={mode} setMode={setMode}
                    accrualField={accrualField} setAccrualField={setAccrualField}
                    rangePreset={rangePreset} setRangePreset={setRangePreset}
                    grouping={grouping} setGrouping={setGrouping}
                    useCustom={useCustom} setUseCustom={setUseCustom}
                    fromCustom={fromCustom} setFromCustom={setFromCustom}
                    toCustom={toCustom} setToCustom={setToCustom}
                    statusIn={statusIn} setStatusIn={setStatusIn}
                    modeLocked
                    showStatusFilters={false}
                />

                {/* Optional error banner */}
                {error && (
                    <div style={{padding:12, background:"#fffbeb", border:"1px solid #f59e0b", borderRadius:8, color:"#92400e"}}>
                    {error}
                    </div>
                )}

                <Grid container spacing={2}>
                    <Grid item xs={12} md={8}>
                        <RevenueTrendChart
                            subheader={`Grouped by ${grouping}${error ? " • " + error : ""}`}
                            series={series}
                            currency="USD"
                            totalRevenue={data?.kpis?.gross || 0}
                            mode={mode}
                            accrualField={accrualField}
                            grouping={grouping}
                        />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <PaymentList
                            entries={revenueEntries}
                            currency="USD"
                            mode="accrual"
                            title="Revenue by Invoice"
                            maxInvoices={revenueEntries.length}
                            groupByPeriod
                            grouping={grouping}
                        />
                    </Grid>
                </Grid>
                <Grid container spacing={2}>
                    <Grid item xs={12} md={8}>
                        <RevenueTrendChart
                            title="Profit Trend"
                            subheader={`Grouped by ${grouping}${error ? " • " + error : ""}`}
                            series={profitSeries}
                            currency="USD"
                            totalRevenue={totalProfit}
                            mode="accrual"
                            grouping={grouping}
                            areaColor="#16a34a"
                        />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <PaymentList
                            title="Profit by Invoice"
                            entries={profitEntries}
                            currency="USD"
                            mode="accrual"
                            maxInvoices={profitEntries.length}
                            groupByPeriod
                            grouping={grouping}
                        />
                    </Grid>
                </Grid>
                <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                        {renderPcModelSearchWidget()}
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <div style={{ border: "1px solid #e5e7eb", borderRadius: 10, padding: 16, background: "#fff" }}>
                            <h3 style={{ margin: "0 0 12px 0" }}>Product Search</h3>
                            <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 10, alignItems: "center" }}>
                                <TextField
                                    label="Model Number"
                                    value={productSearchModel}
                                    onChange={(e) => setProductSearchModel(e.target.value)}
                                    variant="outlined"
                                    size="small"
                                />
                                <TextField
                                    label="From"
                                    type="date"
                                    value={productSearchFrom}
                                    onChange={(e) => setProductSearchFrom(e.target.value)}
                                    variant="outlined"
                                    size="small"
                                    InputLabelProps={{ shrink: true }}
                                />
                                <TextField
                                    label="To"
                                    type="date"
                                    value={productSearchTo}
                                    onChange={(e) => setProductSearchTo(e.target.value)}
                                    variant="outlined"
                                    size="small"
                                    InputLabelProps={{ shrink: true }}
                                />
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={runProductSearch}
                                    disabled={productSearchLoading}
                                    style={{ minHeight: 40 }}
                                >
                                    {productSearchLoading ? "Searching..." : "Search"}
                                </Button>
                            </div>
                            {productSearchError ? (
                                <div style={{ marginTop: 10, color: "#b91c1c" }}>{productSearchError}</div>
                            ) : null}
                            {productSearchResult?.summary ? (
                                <div style={{ marginTop: 14 }}>
                                    <Grid container spacing={2}>
                                        <Grid item xs={6} md={2}><strong>Qty Sold:</strong><div>{Math.round(productSearchResult.summary.quantitySold || 0)}</div></Grid>
                                        <Grid item xs={6} md={2}><strong>Revenue:</strong><div>{currencyPreciseFmt(productSearchResult.summary.revenue || 0, "USD")}</div></Grid>
                                        <Grid item xs={6} md={2}><strong>Profit:</strong><div>{currencyPreciseFmt(productSearchResult.summary.profit || 0, "USD")}</div></Grid>
                                        <Grid item xs={6} md={2}><strong>Cost:</strong><div>{currencyPreciseFmt(productSearchResult.summary.cost || 0, "USD")}</div></Grid>
                                        <Grid item xs={6} md={2}><strong>Invoices:</strong><div>{productSearchResult.summary.invoiceCount || 0}</div></Grid>
                                        <Grid item xs={6} md={2}><strong>Customers:</strong><div>{productSearchResult.summary.customersCount || 0}</div></Grid>
                                    </Grid>
                                    <div style={{ marginTop: 8, color: "#4b5563", fontSize: 13 }}>
                                        Avg Unit Price: {currencyPreciseFmt(productSearchResult.summary.avgUnitPrice || 0, "USD")} | Avg Unit Profit: {currencyPreciseFmt(productSearchResult.summary.avgUnitProfit || 0, "USD")}
                                    </div>
                                    {Array.isArray(productSearchResult.invoices) && productSearchResult.invoices.length ? (
                                        <div style={{ marginTop: 10, maxHeight: 260, overflow: "auto", border: "1px solid #e5e7eb", borderRadius: 8 }}>
                                            <div
                                                style={{
                                                    display: "grid",
                                                    gridTemplateColumns: "1.4fr 110px 90px 130px 130px",
                                                    gap: 8,
                                                    padding: "10px 12px",
                                                    fontSize: 12,
                                                    fontWeight: 700,
                                                    color: "#374151",
                                                    background: "#f9fafb",
                                                    borderBottom: "1px solid #e5e7eb",
                                                    position: "sticky",
                                                    top: 0,
                                                    zIndex: 1,
                                                }}
                                            >
                                                <div>Customer</div>
                                                <div>Date</div>
                                                <div>Qty</div>
                                                <div>Revenue</div>
                                                <div>Profit</div>
                                            </div>
                                            {productSearchResult.invoices.map((row, idx) => (
                                                <div
                                                    key={String(row.invoiceId)}
                                                    role={row.invoiceId ? "button" : undefined}
                                                    tabIndex={row.invoiceId ? 0 : undefined}
                                                    onClick={() => row.invoiceId && history.push(`/invoice/${row.invoiceId}`)}
                                                    onKeyDown={(event) => {
                                                        if (!row.invoiceId) return;
                                                        if (event.key === "Enter" || event.key === " ") {
                                                            event.preventDefault();
                                                            history.push(`/invoice/${row.invoiceId}`);
                                                        }
                                                    }}
                                                    style={{
                                                        display: "grid",
                                                        gridTemplateColumns: "1.4fr 110px 90px 130px 130px",
                                                        gap: 8,
                                                        padding: "9px 12px",
                                                        fontSize: 13,
                                                        background: idx % 2 === 0 ? "#ffffff" : "#fcfcfd",
                                                        borderBottom: "1px solid #f3f4f6",
                                                        cursor: row.invoiceId ? "pointer" : "default",
                                                    }}
                                                >
                                                    <div>{row.customer || "Unknown"}</div>
                                                    <div>{shortDate(row.date)}</div>
                                                    <div>{Math.round(row.quantity || 0)}</div>
                                                    <div>{currencyPreciseFmt(row.revenue || 0, "USD")}</div>
                                                    <div>{currencyPreciseFmt(row.profit || 0, "USD")}</div>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <div style={{ marginTop: 10, color: "#6b7280" }}>No matching invoice line items found for this search.</div>
                                    )}
                                </div>
                            ) : null}
                        </div>
                    </Grid>
                </Grid>

                <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                    <LeaderboardList title={`Top 50 Products`} rows={topProductsRows} currency="USD" onRowClick={handleProductRowClick} maxRows={50} scrollHeight={420} money={topProductMetric !== "quantity"} headerAction={
                        <ButtonGroup size="small" variant="outlined">
                            <Button onClick={() => setTopProductMetric("revenue")} variant={topProductMetric === "revenue" ? "contained" : "outlined"}>Revenue</Button>
                            <Button onClick={() => setTopProductMetric("quantity")} variant={topProductMetric === "quantity" ? "contained" : "outlined"}>Quantity</Button>
                            <Button onClick={() => setTopProductMetric("profit")} variant={topProductMetric === "profit" ? "contained" : "outlined"}>Profit</Button>
                        </ButtonGroup>
                    } />
                    </Grid>
                    <Grid item xs={12} md={6}>
                    <LeaderboardList title="Top 50 Customers" rows={topCustomersRows} currency="USD" maxRows={50} scrollHeight={420} money={topCustomerMetric !== "quantity"} headerAction={
                        <ButtonGroup size="small" variant="outlined">
                            <Button onClick={() => setTopCustomerMetric("revenue")} variant={topCustomerMetric === "revenue" ? "contained" : "outlined"}>Revenue</Button>
                            <Button onClick={() => setTopCustomerMetric("quantity")} variant={topCustomerMetric === "quantity" ? "contained" : "outlined"}>Quantity</Button>
                            <Button onClick={() => setTopCustomerMetric("profit")} variant={topCustomerMetric === "profit" ? "contained" : "outlined"}>Profit</Button>
                        </ButtonGroup>
                    } />
                    </Grid>
                </Grid>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <RecentPaymentsCard
                            payments={data?.recentPayments || []}
                            currency="USD"
                        />
                    </Grid>
                </Grid>
            </Box>
            </section>
            <section className={styles.stat}>
            <h1 style={{textAlign: 'center'}}> Whites </h1>
            <ul className={styles.autoGrid}>

                        <li className={styles.listItem} >
                            <div>
                                <p>${toCommas(ytdWhiteRevenue.toFixed(2))}</p>
                                <h2>YTD Revenue</h2>
                            </div>
                            <div>
                                <Bag />
                            </div>
                        </li>
                </ul>

            </section>

       
        </div>
    )
}

export default Dashboard
